export const content = {
  meta: {
    title: 'Clothing & Shoe Size Converter | EU, UK, US Size Chart',
    description:
      'Free clothing & shoe size converter: EU to US & UK shoe sizes, men\'s & women\'s XS–XXL charts, jeans waist sizes & kids\' shoes by cm. Instant, no signup.',
  },
    hero: {
      h1: 'Clothing and Shoe Size Converter',
      intro:
        'Use this clothing and shoe size converter to quickly compare EU, UK, US and international sizes. Choose a category, enter your size and get the closest matching sizes in other systems.',
      disclaimer:
        'Size conversions are approximate. Brands may use slightly different size charts — always check the manufacturer’s guide before buying.',
    },
    converter: {
      category: 'Choose category',
      gender: 'Gender',
      system: 'Your size system',
      size: 'Your size',
      sizePlaceholder: 'Select or type your size',
      convert: 'Convert',
      resultsTitle: 'Matching sizes',
      recommendation:
        'Recommendation: if you are between two sizes, choosing the larger size is usually safer, especially for shoes and fitted clothing.',
      noMatch: 'No exact match found. Try a nearby size or another system.',
      approximate: 'Approximate / guide only',
      quickPicks: 'Popular sizes — tap to try',
      steps: [
        { id: 'cat', label: 'Category' },
        { id: 'sys', label: 'System' },
        { id: 'size', label: 'Your size' },
        { id: 'result', label: 'Results' },
      ],
      modes: {
        convert: 'I know my size',
        measure: 'Find by cm measurement',
        compare: 'Compare two sizes',
      },
      yourInput: 'You entered',
      cta: 'Start converting',
      toolHeading: 'Interactive size converter tool',
    },
    compare: {
      hint: 'See two sizes side by side — useful when deciding between options online.',
      sizeA: 'First size',
      sizeB: 'Second size',
      resultA: 'Size A equivalents',
      resultB: 'Size B equivalents',
    },
    brandNotes: {
      title: 'Shopping from a specific brand?',
    },
    recent: {
      title: 'Recent conversions',
      clear: 'Clear',
    },
    betweenSizes: {
      chart: 'You are on a chart boundary — you may be between',
      measurement: 'Your measurement sits between two sizes — consider',
    },
    confidence: {
      exact: 'Chart match',
      close: 'Close match',
      estimate: 'Estimated from cm',
    },
    footRuler: {
      label: 'Drag to set foot length (or type above)',
    },
    nav: {
      converter: 'Converter',
      tips: 'Tips',
      shoes: 'Shoes',
      mens: "Men's",
      womens: "Women's",
      jeans: 'Jeans',
      kids: 'Kids',
      measure: 'How to measure',
      faq: 'FAQ',
    },
    measureFinder: {
      title: 'Find size from body measurement',
      hint: 'Enter centimetres — we match the closest size row in our chart.',
      chest: 'Chest',
      waist: 'Waist',
      footPlaceholder: 'e.g. 26.5',
      bodyPlaceholder: 'e.g. 96',
      find: 'Find my size',
    },
    fitAdvisor: {
      title: 'How do you like your fit?',
      snug: 'Snug / tight',
      regular: 'Regular',
      loose: 'Relaxed / loose',
      trySize: 'Try suggested size',
      regularHint: 'Your converted size is a good starting point for a regular fit.',
    },
    neighbors: { label: 'Nearby sizes in chart' },
    actions: {
      copyResults: 'Copy sizes',
      copyLink: 'Copy share link',
      copied: 'Copied!',
      print: 'Print size card',
    },
    tips: [
      {
        title: 'Shop across borders',
        text: 'EU, UK and US labels on the same product often differ — always convert before checkout.',
      },
      {
        title: 'Check the brand chart',
        text: 'Nike, Adidas, Zara and ASOS each publish their own fit guides. Use this tool first, then confirm on the brand site.',
      },
      {
        title: 'Between two sizes?',
        text: 'For shoes and outerwear, half a size up reduces returns. For stretchy items, your usual size may work.',
      },
      {
        title: 'Measure in the evening',
        text: 'Feet swell slightly during the day — measuring in the afternoon gives a safer shoe size.',
      },
    ],
    table: {
      clickHint: 'Click any row to load it in the converter above',
      activeRow: 'Selected in converter',
    },
    systems: {
      eu: 'EU',
      uk: 'UK',
      us: 'US',
      cm: 'CM (foot length)',
      international: 'International (XS–XXL)',
      chestCm: 'Chest (cm)',
      waistCm: 'Waist (cm)',
      hipsCm: 'Hips (cm)',
      waistIn: 'Waist (inches)',
      inseamCm: 'Inseam (cm)',
    },
    results: {
      eu: 'EU',
      uk: 'UK',
      us: 'US',
      cm: 'Foot length',
      international: 'International',
      chestCm: 'Chest',
      waistCm: 'Waist',
      hipsCm: 'Hips',
      waistIn: 'Waist (in)',
      inseamCm: 'Inseam',
      age: 'Typical age',
    },
    sections: {
      howItWorks: 'How does the size converter work?',
      howItWorksText:
        'Select a category (shoes, clothing or jeans), choose your gender when needed, pick the size system you know (EU, UK, US, cm or international), then enter your size. The tool looks up the closest row in our reference chart and shows equivalent sizes in other systems.',
      shoes: 'Shoe size conversion: EU, UK and US',
      shoesText:
        'Men’s and women’s shoe sizes differ, especially in the US system. EU sizes are numeric; UK and US use different scales. Foot length in centimetres helps when shopping online.',
      mens: "Men's clothing size chart",
      mensText:
        'Men’s tops are often sized by chest and waist in centimetres, plus EU numeric sizes and international XS–XXL labels.',
      womens: "Women's clothing size chart",
      womensText:
        'Women’s sizing varies more by brand. EU, UK and US numeric sizes and body measurements (chest, waist, hips) help compare fits.',
      kids: "Kids' shoe size chart",
      kidsText:
        'Children’s shoe sizes depend on foot length (cm) and age. Brands differ — use cm for the best match, then confirm on the product page.',
      jeans: 'Jeans & pants size chart (waist EU, UK, US)',
      jeansText:
        'Jeans are often sold by waist in inches (W) and inseam (L). Match your waist measurement to EU and US labels before ordering denim online.',
      measure: 'How to measure correctly',
      measureItems: [
        { title: 'Chest', text: 'Measure around the fullest part of your chest, keeping the tape horizontal.' },
        { title: 'Waist', text: 'Measure around your natural waist, usually just above the belly button.' },
        { title: 'Hips', text: 'Measure around the fullest part of your hips.' },
        { title: 'Foot length', text: 'Stand on paper, mark heel and longest toe, measure the distance in cm.' },
        { title: 'Inseam', text: 'Measure from the crotch along the inside of the leg to the ankle.' },
      ],
      seo: 'About this size converter',
      seoText:
        'This clothing and shoe size converter helps you find the right fit when shopping online from EU, UK or US stores. Convert EU shoe size to US or UK, compare men\'s and women\'s EU clothing sizes to international S/M/L/XL, and look up jeans waist sizes in inches or centimetres. Use foot length in cm for kids\' shoes. Results are approximate — Nike, Adidas, Zara and other brands may differ, so always check the official size guide before you buy.',
    tipsSectionTitle: 'Size conversion tips',
      tableTitle: 'Full size chart',
      faq: 'Frequently asked questions',
    },
    faq: [
      {
        q: 'How do I convert EU shoe size to US or UK?',
        a: 'Select the shoe category, choose your gender, enter the EU size and the converter will show the closest US and UK size match.',
      },
      {
        q: "Are men's and women's shoe sizes different?",
        a: 'Yes. US shoe sizes are different for men and women, so always choose the correct gender before converting.',
      },
      {
        q: 'Is EU 42 always the same US size?',
        a: 'Not always. Size conversions are approximate because brands may use slightly different size charts.',
      },
      {
        q: 'What should I do if I am between two sizes?',
        a: 'If you are between two sizes, choosing the larger size is usually safer, especially for shoes, jackets and fitted clothing.',
      },
      {
        q: 'What is the difference between XS, S, M, L and EU sizes?',
        a: 'XS, S, M and L are international size labels, while EU sizes usually use numbers. The converter helps match both systems.',
      },
      {
        q: "Can I use this converter for kids' clothing?",
        a: "Yes, but kids' sizes often depend on height, age and body measurements, so the result should be used as a guide.",
      },
    ],
};

export function getContent() {
  return content;
}
